<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button (click)="goBack()">
        <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title>Taxonomy Dimensions</ion-title>
  </ion-toolbar>
</ion-header>

<ion-content [fullscreen]="true">
  <ion-grid style="margin: 3px">
    <!-- Error State -->
    <ion-row *ngIf="error">
      <ion-col>
        <ion-card color="danger">
          <ion-card-header>
            <ion-card-title>Error</ion-card-title>
          </ion-card-header>
          <ion-card-content>
            {{ error }}
            <br />
            <ion-button
              (click)="loadDimensions()"
              color="light"
              size="small"
              style="margin-top: 1rem"
            >
              <ion-icon name="refresh-outline" slot="start"></ion-icon>
              Try Again
            </ion-button>
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>

    <!-- Tenant Header -->
    <ion-row
      *ngIf="tenant"
      class="ion-justify-content-between ion-align-items-center"
      style="margin-bottom: 2rem"
    >
      <ion-col>
        <h1>Taxonomy Dimensions for {{ tenant.name }}</h1>
        <p style="margin: 0.25rem 0 0 0; font-size: 0.9rem">
          Manage and configure taxonomy dimensions for automated article
          categorization
        </p>
      </ion-col>
      <ion-col size="auto">
        <ion-button (click)="openCreateDimensionModal()" color="primary">
          <ion-icon name="add" slot="start"></ion-icon>
          Create Dimension
        </ion-button>
      </ion-col>
    </ion-row>

    <!-- Loading State -->
    <ion-row *ngIf="isLoadingDimensions">
      <ion-col class="ion-text-center">
        <ion-spinner></ion-spinner>
        <p>Loading dimensions...</p>
      </ion-col>
    </ion-row>

    <!-- Dimensions List -->
    <ion-row *ngIf="!isLoadingDimensions && !error">
      <ion-col size="12">
        <ion-card *ngFor="let dimension of dimensions">
          <ion-card-header>
            <div
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
              "
            >
              <ion-card-title>{{ dimension.name }}</ion-card-title>
              <div>
                <ion-chip
                  [color]="getRoleColor(dimension.role)"
                  *ngIf="dimension.role"
                >
                  {{ dimension.role }}
                </ion-chip>
                <ion-chip [color]="getStrategyColor(dimension.strategy)">
                  {{ dimension.strategy }}
                </ion-chip>
              </div>
            </div>
          </ion-card-header>
          <ion-card-content>
            <div
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
              "
            >
              <div
                style="flex: 1"
                (click)="selectDimension(dimension)"
                class="clickable"
              >
                <p style="margin: 0; color: var(--ion-color-medium)">
                  Strategy: {{ dimension.strategy }} | Active:
                  {{ dimension.isActive ? "Yes" : "No" }}
                </p>
                <p
                  style="
                    margin: 0;
                    font-size: 0.85rem;
                    color: var(--ion-color-medium);
                  "
                >
                  LLM: {{ dimension.llmThreshold }} | Centroid:
                  {{ dimension.centroidThreshold }} | Aggregation:
                  {{ dimension.aggregationThreshold }}
                </p>
                <p
                  style="
                    margin: 0;
                    font-size: 0.8rem;
                    color: var(--ion-color-medium);
                    opacity: 0.8;
                  "
                >
                  Created: {{ dimension.createdAt | date: "short" }}
                </p>
              </div>
              <div style="display: flex; align-items: center; gap: 0.5rem">
                <ion-button
                  fill="clear"
                  size="small"
                  (click)="openEditDimensionModal(dimension)"
                  color="primary"
                >
                  <ion-icon name="pencil" slot="icon-only"></ion-icon>
                </ion-button>
                <ion-button
                  fill="clear"
                  size="small"
                  (click)="selectDimension(dimension)"
                  color="medium"
                >
                  <ion-icon name="chevron-forward" slot="icon-only"></ion-icon>
                </ion-button>
              </div>
            </div>
          </ion-card-content>
        </ion-card>

        <!-- Empty State -->
        <ion-card *ngIf="dimensions.length === 0">
          <ion-card-content class="ion-text-center">
            <ion-icon
              name="layers-outline"
              size="large"
              color="medium"
            ></ion-icon>
            <h3>No dimensions found</h3>
            <p>
              Create your first taxonomy dimension to get started with automated
              article categorization.
            </p>
            <ion-button (click)="openCreateDimensionModal()" color="primary">
              <ion-icon name="add" slot="start"></ion-icon>
              Create Dimension
            </ion-button>
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>
  </ion-grid>

  <!-- Create Dimension Modal -->
  <ion-modal
    [isOpen]="showCreateDimensionModal"
    (didDismiss)="cancelCreateDimension()"
  >
    <ng-template>
      <ion-header>
        <ion-toolbar>
          <ion-title>Create New Dimension</ion-title>
          <ion-buttons slot="end">
            <ion-button (click)="cancelCreateDimension()">
              <ion-icon name="close" slot="icon-only"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <ion-item>
          <ion-label position="stacked">Name *</ion-label>
          <ion-input
            [value]="newDimensionForm.name"
            (ionInput)="newDimensionForm.name = $event.target.value"
            placeholder="Enter dimension name"
          ></ion-input>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Role</ion-label>
          <ion-select
            [value]="newDimensionForm.role"
            (ionChange)="
              newDimensionForm.role = $event.detail.value;
              onRoleChange($event.detail.value)
            "
            placeholder="Select role (optional)"
          >
            <ion-select-option value="primary">Primary</ion-select-option>
            <ion-select-option value="broad">Broad</ion-select-option>
            <ion-select-option value="granular">Granular</ion-select-option>
          </ion-select>
          <ion-note
            slot="helper"
            style="font-size: 0.75rem; color: var(--ion-color-medium)"
          >
            <div *ngIf="newDimensionForm.role === 'broad'">
              Optimized for creating broader, high-level categories (e.g.,
              "Documentation", "Troubleshooting"). Uses high centroid threshold
              for grouping similar content.
            </div>
            <div *ngIf="newDimensionForm.role === 'granular'">
              Optimized for creating specific, detailed categories (e.g., "REST
              API Authentication", "SSL Certificate Installation"). Uses high
              LLM threshold for precision.
            </div>
            <div *ngIf="newDimensionForm.role === 'primary'">
              Standard configuration for general-purpose categorization using
              full-article analysis.
            </div>
          </ion-note>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Strategy *</ion-label>
          <ion-select
            [value]="newDimensionForm.strategy"
            (ionChange)="
              newDimensionForm.strategy = $event.detail.value;
              onStrategyChange($event.detail.value)
            "
            placeholder="Select strategy"
          >
            <ion-select-option value="large"
              >Large - Full article LLM analysis</ion-select-option
            >
            <ion-select-option value="small-llm"
              >Small LLM - Chunk-based LLM classification</ion-select-option
            >
            <ion-select-option value="small-centroid"
              >Small Centroid - Vector similarity only</ion-select-option
            >
            <ion-select-option value="small-hybrid"
              >Small Hybrid - LLM + vector similarity</ion-select-option
            >
          </ion-select>
          <ion-note
            slot="helper"
            style="font-size: 0.75rem; color: var(--ion-color-medium)"
          >
            <div *ngIf="newDimensionForm.strategy === 'large'">
              Analyzes entire article content with AI model. Best for
              comprehensive categorization of long content.
            </div>
            <div *ngIf="newDimensionForm.strategy === 'small-llm'">
              Splits articles into chunks, classifies each with AI, then
              aggregates results. Good for detailed analysis.
            </div>
            <div *ngIf="newDimensionForm.strategy === 'small-centroid'">
              Uses vector similarity matching against existing category
              patterns. Fast, requires existing categories.
            </div>
            <div *ngIf="newDimensionForm.strategy === 'small-hybrid'">
              Combines vector similarity and AI classification. Tries vector
              matching first, falls back to AI.
            </div>
          </ion-note>
        </ion-item>

        <div style="margin-top: 1.5rem">
          <h4 style="margin: 0 0 1rem 0; font-size: 0.9rem; font-weight: 600">
            Confidence Thresholds
          </h4>

          <ion-item>
            <ion-label
              position="stacked"
              style="color: var(--ion-color-primary)"
              >LLM Threshold</ion-label
            >
            <ion-input
              type="number"
              step="0.01"
              min="0"
              max="1"
              [value]="newDimensionForm.llmThreshold"
              (ionInput)="newDimensionForm.llmThreshold = $event.target.value"
            ></ion-input>
            <ion-note
              slot="helper"
              style="font-size: 0.75rem; color: var(--ion-color-medium)"
            >
              <div
                *ngIf="
                  newDimensionForm.strategy === 'large' ||
                  newDimensionForm.strategy === 'small-llm' ||
                  newDimensionForm.strategy === 'small-hybrid'
                "
              >
                Minimum confidence required for AI model classifications. Lower
                = more categories, higher = fewer but more certain.
              </div>
              <div *ngIf="newDimensionForm.strategy === 'small-centroid'">
                Not used by this strategy (vector similarity only).
              </div>
            </ion-note>
          </ion-item>

          <ion-item>
            <ion-label
              position="stacked"
              style="color: var(--ion-color-secondary)"
              >Centroid Threshold</ion-label
            >
            <ion-input
              type="number"
              step="0.01"
              min="0"
              max="1"
              [value]="newDimensionForm.centroidThreshold"
              (ionInput)="
                newDimensionForm.centroidThreshold = $event.target.value
              "
            ></ion-input>
            <ion-note
              slot="helper"
              style="font-size: 0.75rem; color: var(--ion-color-medium)"
            >
              <div
                *ngIf="
                  newDimensionForm.strategy === 'small-centroid' ||
                  newDimensionForm.strategy === 'small-hybrid'
                "
              >
                Minimum confidence for vector similarity matching. Higher values
                = stricter matching against existing category patterns.
              </div>
              <div
                *ngIf="
                  newDimensionForm.strategy === 'large' ||
                  newDimensionForm.strategy === 'small-llm'
                "
              >
                Not used by this strategy (no vector similarity component).
              </div>
            </ion-note>
          </ion-item>

          <ion-item>
            <ion-label
              position="stacked"
              style="color: var(--ion-color-tertiary)"
              >Aggregation Threshold</ion-label
            >
            <ion-input
              type="number"
              step="0.01"
              min="0"
              max="1"
              [value]="newDimensionForm.aggregationThreshold"
              (ionInput)="
                newDimensionForm.aggregationThreshold = $event.target.value
              "
            ></ion-input>
            <ion-note
              slot="helper"
              style="font-size: 0.75rem; color: var(--ion-color-medium)"
            >
              <div
                *ngIf="
                  newDimensionForm.strategy === 'small-llm' ||
                  newDimensionForm.strategy === 'small-centroid' ||
                  newDimensionForm.strategy === 'small-hybrid'
                "
              >
                Minimum confidence when combining chunk classifications into
                article categories. Lower = more categories, higher = fewer but
                more certain.
              </div>
              <div *ngIf="newDimensionForm.strategy === 'large'">
                Controls final categorization confidence for full-article
                analysis. Lower = more categories assigned per article.
              </div>
            </ion-note>
          </ion-item>
        </div>

        <div style="margin-top: 2rem">
          <ion-button
            (click)="createDimension()"
            expand="block"
            color="primary"
          >
            Create Dimension
          </ion-button>
          <ion-button
            (click)="cancelCreateDimension()"
            expand="block"
            fill="clear"
          >
            Cancel
          </ion-button>
        </div>
      </ion-content>
    </ng-template>
  </ion-modal>

  <!-- Edit Dimension Modal -->
  <ion-modal
    [isOpen]="showEditDimensionModal"
    (didDismiss)="cancelEditDimension()"
  >
    <ng-template>
      <ion-header>
        <ion-toolbar>
          <ion-title>Edit Dimension: {{ editingDimension?.name }}</ion-title>
          <ion-buttons slot="end">
            <ion-button (click)="cancelEditDimension()">
              <ion-icon name="close" slot="icon-only"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <ion-item>
          <ion-label position="stacked">Name *</ion-label>
          <ion-input
            [value]="editDimensionForm.name"
            (ionInput)="editDimensionForm.name = $event.target.value"
            placeholder="Enter dimension name"
          ></ion-input>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Role</ion-label>
          <ion-select
            [value]="editDimensionForm.role"
            (ionChange)="
              editDimensionForm.role = $event.detail.value;
              onEditRoleChange($event.detail.value)
            "
            placeholder="Select role (optional)"
          >
            <ion-select-option value="">None</ion-select-option>
            <ion-select-option value="primary">Primary</ion-select-option>
            <ion-select-option value="broad">Broad</ion-select-option>
            <ion-select-option value="granular">Granular</ion-select-option>
          </ion-select>
          <ion-note
            slot="helper"
            style="font-size: 0.75rem; color: var(--ion-color-medium)"
          >
            <div *ngIf="editDimensionForm.role === 'broad'">
              Optimized for creating broader, high-level categories (e.g.,
              "Documentation", "Troubleshooting"). Uses high centroid threshold
              for grouping similar content.
            </div>
            <div *ngIf="editDimensionForm.role === 'granular'">
              Optimized for creating specific, detailed categories (e.g., "REST
              API Authentication", "SSL Certificate Installation"). Uses high
              LLM threshold for precision.
            </div>
            <div *ngIf="editDimensionForm.role === 'primary'">
              Standard configuration for general-purpose categorization using
              full-article analysis.
            </div>
          </ion-note>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Strategy *</ion-label>
          <ion-select
            [value]="editDimensionForm.strategy"
            (ionChange)="
              editDimensionForm.strategy = $event.detail.value;
              onEditStrategyChange($event.detail.value)
            "
            placeholder="Select strategy"
          >
            <ion-select-option value="large"
              >Large - Full article LLM analysis</ion-select-option
            >
            <ion-select-option value="small-llm"
              >Small LLM - Chunk-based LLM classification</ion-select-option
            >
            <ion-select-option value="small-centroid"
              >Small Centroid - Vector similarity only</ion-select-option
            >
            <ion-select-option value="small-hybrid"
              >Small Hybrid - LLM + vector similarity</ion-select-option
            >
          </ion-select>
          <ion-note
            slot="helper"
            style="font-size: 0.75rem; color: var(--ion-color-medium)"
          >
            <div *ngIf="editDimensionForm.strategy === 'large'">
              Analyzes entire article content with AI model. Best for
              comprehensive categorization of long content.
            </div>
            <div *ngIf="editDimensionForm.strategy === 'small-llm'">
              Splits articles into chunks, classifies each with AI, then
              aggregates results. Good for detailed analysis.
            </div>
            <div *ngIf="editDimensionForm.strategy === 'small-centroid'">
              Uses vector similarity matching against existing category
              patterns. Fast, requires existing categories.
            </div>
            <div *ngIf="editDimensionForm.strategy === 'small-hybrid'">
              Combines vector similarity and AI classification. Tries vector
              matching first, falls back to AI.
            </div>
          </ion-note>
        </ion-item>

        <ion-item>
          <ion-label position="stacked">Status *</ion-label>
          <ion-select
            [value]="editDimensionForm.isActive"
            (ionChange)="editDimensionForm.isActive = $event.detail.value"
            placeholder="Select status"
          >
            <ion-select-option [value]="true">Active</ion-select-option>
            <ion-select-option [value]="false">Inactive</ion-select-option>
          </ion-select>
          <ion-note
            slot="helper"
            style="font-size: 0.75rem; color: var(--ion-color-medium)"
          >
            Only active dimensions are used for article classification.
          </ion-note>
        </ion-item>

        <div style="margin-top: 1.5rem">
          <h4 style="margin: 0 0 1rem 0; font-size: 0.9rem; font-weight: 600">
            Confidence Thresholds
          </h4>

          <ion-item>
            <ion-label
              position="stacked"
              style="color: var(--ion-color-primary)"
              >LLM Threshold</ion-label
            >
            <ion-input
              type="number"
              step="0.01"
              min="0"
              max="1"
              [value]="editDimensionForm.llmThreshold"
              (ionInput)="editDimensionForm.llmThreshold = $event.target.value"
            ></ion-input>
            <ion-note
              slot="helper"
              style="font-size: 0.75rem; color: var(--ion-color-medium)"
            >
              <div
                *ngIf="
                  editDimensionForm.strategy === 'large' ||
                  editDimensionForm.strategy === 'small-llm' ||
                  editDimensionForm.strategy === 'small-hybrid'
                "
              >
                Minimum confidence required for AI model classifications. Lower
                = more categories, higher = fewer but more certain.
              </div>
              <div *ngIf="editDimensionForm.strategy === 'small-centroid'">
                Not used by this strategy (vector similarity only).
              </div>
            </ion-note>
          </ion-item>

          <ion-item>
            <ion-label
              position="stacked"
              style="color: var(--ion-color-secondary)"
              >Centroid Threshold</ion-label
            >
            <ion-input
              type="number"
              step="0.01"
              min="0"
              max="1"
              [value]="editDimensionForm.centroidThreshold"
              (ionInput)="
                editDimensionForm.centroidThreshold = $event.target.value
              "
            ></ion-input>
            <ion-note
              slot="helper"
              style="font-size: 0.75rem; color: var(--ion-color-medium)"
            >
              <div
                *ngIf="
                  editDimensionForm.strategy === 'small-centroid' ||
                  editDimensionForm.strategy === 'small-hybrid'
                "
              >
                Minimum confidence for vector similarity matching. Higher values
                = stricter matching against existing category patterns.
              </div>
              <div
                *ngIf="
                  editDimensionForm.strategy === 'large' ||
                  editDimensionForm.strategy === 'small-llm'
                "
              >
                Not used by this strategy (no vector similarity component).
              </div>
            </ion-note>
          </ion-item>

          <ion-item>
            <ion-label
              position="stacked"
              style="color: var(--ion-color-tertiary)"
              >Aggregation Threshold</ion-label
            >
            <ion-input
              type="number"
              step="0.01"
              min="0"
              max="1"
              [value]="editDimensionForm.aggregationThreshold"
              (ionInput)="
                editDimensionForm.aggregationThreshold = $event.target.value
              "
            ></ion-input>
            <ion-note
              slot="helper"
              style="font-size: 0.75rem; color: var(--ion-color-medium)"
            >
              <div
                *ngIf="
                  editDimensionForm.strategy === 'small-llm' ||
                  editDimensionForm.strategy === 'small-centroid' ||
                  editDimensionForm.strategy === 'small-hybrid'
                "
              >
                Minimum confidence when combining chunk classifications into
                article categories. Lower = more categories, higher = fewer but
                more certain.
              </div>
              <div *ngIf="editDimensionForm.strategy === 'large'">
                Controls final categorization confidence for full-article
                analysis. Lower = more categories assigned per article.
              </div>
            </ion-note>
          </ion-item>
        </div>

        <div style="margin-top: 2rem">
          <ion-button
            (click)="updateDimension()"
            expand="block"
            color="primary"
          >
            Update Dimension
          </ion-button>
          <ion-button
            (click)="cancelEditDimension()"
            expand="block"
            fill="clear"
          >
            Cancel
          </ion-button>
        </div>
      </ion-content>
    </ng-template>
  </ion-modal>
</ion-content>
